// Static demo-mode fill-in data for Timeline.component.jsx.
// Shapes must match exactly what this view's own rendering expects — see
// TimelineComponent's `sessionsByDay.map(...)` block, DayTimelineCard.component.jsx
// (which reads `day.totals.*` directly), and Timeline.config.jsx's
// `timelineTableConfig` cell renderers (which read `row.*` per session).
//
// NOTE: this shape is a superset of DashboardOverview's DEMO_SESSIONS_BY_DAY —
// DayTimelineCard additionally requires a `totals` object per day, and the
// timeline table additionally reads `productiveSec`, `screenshotsCount`,
// `pastData`/`typeLog` (session source tags) per session that the dashboard
// overview's activity-flow chart never touches.
import { createSequentialImagePicker } from '@/utils/demoScreenshotImages';

// Anchored to "today"/"yesterday"/"the day before" (UTC) so the demo never
// shows a stale fixed date — only the calendar dates shift with real time;
// the times-of-day stay fixed, matching the originally authored session shape.
const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n) => String(n).padStart(2, '0');
const isoDateFromDaysAgo = (daysAgo) => {
  const d = new Date(Date.now() - daysAgo * DAY_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
const dayLabelFromDaysAgo = (daysAgo) =>
  new Date(Date.now() - daysAgo * DAY_MS).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
const DAY0_KEY = isoDateFromDaysAgo(0);
const DAY0_LABEL = dayLabelFromDaysAgo(0);
const DAY1_KEY = isoDateFromDaysAgo(1);
const DAY1_LABEL = dayLabelFromDaysAgo(1);
const DAY2_KEY = isoDateFromDaysAgo(2);
const DAY2_LABEL = dayLabelFromDaysAgo(2);

const pickDemoScreenshotImage = createSequentialImagePicker();

// Builds one ScreenshotRow (matching src/utils/screenshotPrivacy.js exactly:
// { url, thumbUrl, timestamp, activityId, screenIndex }), reusing the same
// next-in-sequence image for both url and thumbUrl.
function buildDemoScreenshotEntry(timestamp, activityId, screenIndex) {
  const image = pickDemoScreenshotImage();
  return { url: image, thumbUrl: image, timestamp, activityId, screenIndex };
}

// Generates `count` ScreenshotRow entries evenly spaced between a session's
// start/end, so the session row's clickable SCREENSHOTS count in
// Timeline.config.jsx and the number of images the modal actually opens with
// always match.
function buildSessionScreenshots(sessionId, start, end, count) {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const span = endMs - startMs;
  return Array.from({ length: count }, (_, i) => {
    const t = count === 1 ? startMs : startMs + (span * i) / (count - 1);
    return buildDemoScreenshotEntry(
      new Date(t).toISOString(),
      `demo-tl-activity-${sessionId}-${i}`,
      0
    );
  });
}

// Consumed by DayTimelineCard (day.totals.*) and timelineTableConfig's
// per-row cellRenderers (row.*). start/end are moment-parseable timestamps;
// activityPct is 0-100; screenshotsCount feeds both the day's ScreenshotsCard
// total and the per-row "SCREENSHOTS" column; pastData/typeLog drive the
// optional "offline"/"manual" SessionSourceTags badges.
// Earnings/activityPct below are computed off this file's default member
// (Ryan Mitchell, EMP-1001, $45/hr — see DEMO_MEMBERS_OPTIONS) so
// earnings = productive hours x rate and activityPct = (duration-idle)/duration
// reconcile exactly with the seconds figures on each day/session.
export const DEMO_SESSIONS_BY_DAY = [
  {
    dayKey: DAY0_KEY,
    dayLabel: DAY0_LABEL,
    totals: {
      durationSec: 27000,
      idleSec: 2100,
      productiveSec: 24900,
      activityPct: 92.22,
      earnings: 311.25,
    },
    sessions: [
      {
        id: 'demo-tl-session-3-1',
        start: `${DAY0_KEY}T09:02:00Z`,
        end: `${DAY0_KEY}T12:47:00Z`,
        durationSec: 13500,
        idleSec: 900,
        productiveSec: 12600,
        activityPct: 93,
        screenshotsCount: 24,
        project: 'Website Redesign',
        task: 'Homepage layout',
        pastData: false,
        typeLog: 'auto',
      },
      {
        id: 'demo-tl-session-3-2',
        start: `${DAY0_KEY}T13:30:00Z`,
        end: `${DAY0_KEY}T17:15:00Z`,
        durationSec: 13500,
        idleSec: 1200,
        productiveSec: 12300,
        activityPct: 91,
        screenshotsCount: 22,
        project: 'Website Redesign',
        task: 'Client feedback review',
        pastData: false,
        typeLog: 'auto',
      },
    ],
  },
  {
    dayKey: DAY1_KEY,
    dayLabel: DAY1_LABEL,
    totals: {
      durationSec: 22500,
      idleSec: 2100,
      productiveSec: 20400,
      activityPct: 90.67,
      earnings: 255.0,
    },
    sessions: [
      {
        id: 'demo-tl-session-2-1',
        start: `${DAY1_KEY}T09:15:00Z`,
        end: `${DAY1_KEY}T12:00:00Z`,
        durationSec: 9900,
        idleSec: 600,
        productiveSec: 9300,
        activityPct: 94,
        screenshotsCount: 18,
        project: 'Mobile App',
        task: 'API integration',
        pastData: false,
        typeLog: 'auto',
      },
      {
        id: 'demo-tl-session-2-2',
        start: `${DAY1_KEY}T13:00:00Z`,
        end: `${DAY1_KEY}T16:30:00Z`,
        durationSec: 12600,
        idleSec: 1500,
        productiveSec: 11100,
        activityPct: 88,
        screenshotsCount: 20,
        project: 'Mobile App',
        task: 'Bug fixes',
        pastData: true,
        typeLog: 'auto',
      },
    ],
  },
  {
    dayKey: DAY2_KEY,
    dayLabel: DAY2_LABEL,
    totals: {
      durationSec: 23400,
      idleSec: 1560,
      productiveSec: 21840,
      activityPct: 93.33,
      earnings: 273.0,
    },
    sessions: [
      {
        id: 'demo-tl-session-1-1',
        start: `${DAY2_KEY}T08:50:00Z`,
        end: `${DAY2_KEY}T11:40:00Z`,
        durationSec: 10200,
        idleSec: 480,
        productiveSec: 9720,
        activityPct: 95,
        screenshotsCount: 16,
        project: 'Internal Tools',
        task: 'Dashboard analytics',
        pastData: false,
        typeLog: 'manual',
      },
      {
        id: 'demo-tl-session-1-2',
        start: `${DAY2_KEY}T12:30:00Z`,
        end: `${DAY2_KEY}T16:10:00Z`,
        durationSec: 13200,
        idleSec: 1080,
        productiveSec: 12120,
        activityPct: 92,
        screenshotsCount: 19,
        project: 'Internal Tools',
        task: 'Code review',
        pastData: false,
        typeLog: 'auto',
      },
    ],
  },
];

// Keyed by session id (must match DEMO_SESSIONS_BY_DAY session ids above).
// Timeline.component.jsx's openScreenshotsModal reads this directly in demo
// mode instead of fetching real session activities — each array's length
// matches its session's screenshotsCount above so the SCREENSHOTS button's
// number and the modal's actual image count agree.
export const DEMO_SESSION_SCREENSHOTS = {
  'demo-tl-session-3-1': buildSessionScreenshots(
    'demo-tl-session-3-1',
    `${DAY0_KEY}T09:02:00Z`,
    `${DAY0_KEY}T12:47:00Z`,
    24
  ),
  'demo-tl-session-3-2': buildSessionScreenshots(
    'demo-tl-session-3-2',
    `${DAY0_KEY}T13:30:00Z`,
    `${DAY0_KEY}T17:15:00Z`,
    22
  ),
  'demo-tl-session-2-1': buildSessionScreenshots(
    'demo-tl-session-2-1',
    `${DAY1_KEY}T09:15:00Z`,
    `${DAY1_KEY}T12:00:00Z`,
    18
  ),
  'demo-tl-session-2-2': buildSessionScreenshots(
    'demo-tl-session-2-2',
    `${DAY1_KEY}T13:00:00Z`,
    `${DAY1_KEY}T16:30:00Z`,
    20
  ),
  'demo-tl-session-1-1': buildSessionScreenshots(
    'demo-tl-session-1-1',
    `${DAY2_KEY}T08:50:00Z`,
    `${DAY2_KEY}T11:40:00Z`,
    16
  ),
  'demo-tl-session-1-2': buildSessionScreenshots(
    'demo-tl-session-1-2',
    `${DAY2_KEY}T12:30:00Z`,
    `${DAY2_KEY}T16:10:00Z`,
    19
  ),
};

// Consumed by ReportFiltersBar's member dropdown ({ value, label }) and by
// getEmployeeIdForUser/getRateForUser (employeeId, rate) — matches the real
// shape produced by useMembersOptionsForSelect.
export const DEMO_MEMBERS_OPTIONS = [
  {
    value: 'demo-user',
    label: 'Ryan Mitchell (EMP-1001)',
    employeeId: 'EMP-1001',
    rate: 45,
  },
  {
    value: 'demo-user-2',
    label: 'Rachel Turner (EMP-1002)',
    employeeId: 'EMP-1002',
    rate: 38,
  },
  {
    value: 'demo-user-3',
    label: 'Michael Chen (EMP-1003)',
    employeeId: 'EMP-1003',
    rate: 52,
  },
];
